/**
 * The small immutable replacements every applier needs, and the two result shapes.
 *
 * A score is a tree of frozen-by-convention objects, so changing a note means rebuilding
 * the spine above it: the voice, the bar, the part, the score. Done inline in each
 * applier that rebuilding is four nested spreads of noise around one interesting line,
 * and every copy of it is a chance to drop a sibling. It is done once here instead.
 */

import type { MeasureId, PartId } from "../document/ids";
import type { Part, PartMeasure, Score, Voice } from "../document/types";
import type { Command, CommandResult } from "./types";

/** A command that changed nothing, and the reason. Never throws: refusal is a value. */
export function fail(command: Command, reason: string): CommandResult {
  return { ok: false, reason, command };
}

/** A command that applied, with the command that takes it back. */
export function done(score: Score, inverse: Command): CommandResult {
  return { ok: true, score, inverse };
}

/** The part at `id`, or `null`. */
export function findPart(score: Score, id: PartId): Part | null {
  return score.parts.find((part) => part.id === id) ?? null;
}

/** A score with one part swapped for a new version of itself. */
export function replacePart(score: Score, part: Part): Score {
  return {
    ...score,
    parts: score.parts.map((existing) => (existing.id === part.id ? part : existing)),
  };
}

/** A score with one bar of one part swapped, or `null` when the part or bar is not there. */
export function replacePartMeasure(
  score: Score,
  partId: PartId,
  measureId: MeasureId,
  measure: PartMeasure,
): Score | null {
  const part = findPart(score, partId);
  if (part === null || part.measures[measureId] === undefined) return null;
  return replacePart(score, { ...part, measures: { ...part.measures, [measureId]: measure } });
}

/** A bar with one voice rebuilt by `change`, or `null` when the voice index is outside it. */
export function withVoice(
  measure: PartMeasure,
  voiceIndex: number,
  change: (voice: Voice) => Voice,
): PartMeasure | null {
  const voice = measure.voices[voiceIndex];
  if (voice === undefined) return null;
  const voices = [...measure.voices];
  voices[voiceIndex] = change(voice);
  return { ...measure, voices };
}

/**
 * A part with every trace of some events gone: the events themselves, the directions hung
 * on them and the spanners that ended on them.
 *
 * A slur whose end note has been deleted has nowhere to be drawn, and a dynamic anchored
 * to a gone event would be invisible but still exported. Both are dropped at the moment
 * the event goes, which is also why deleting a note inverts to a snapshot: putting the
 * note back has to put them back too.
 */
export function withoutEvents(part: Part, gone: ReadonlySet<string>): Part {
  const measures: Record<MeasureId, PartMeasure> = {};
  for (const [id, measure] of Object.entries(part.measures) as [MeasureId, PartMeasure][]) {
    measures[id] = {
      ...measure,
      voices: measure.voices.map((voice) => ({
        ...voice,
        events: voice.events.filter((event) => !gone.has(event.id)),
      })),
      directions: measure.directions.filter((direction) => !gone.has(direction.anchor)),
    };
  }

  return {
    ...part,
    measures,
    spanners: part.spanners.filter((span) => !gone.has(span.from) && !gone.has(span.to)),
  };
}
